import { useCallback, useMemo } from 'react';
import { Control, FieldName, useController } from 'react-hook-form';

import { ICD9Procedure } from '@dreryk/egabinet-icd-dictionary-frontend-api';

import { Bean } from '@dreryk/react-common';

import { requiredValidator } from '../common.utils';

type Icd9ProcedureSelectorFormBean = {
   ic9Procedures: ICD9Procedure[];
};

export type AutosuggestSelectorReturnType<FIELD_TYPE extends Bean> = {
   addItem: (item: FIELD_TYPE) => void;
   removeItem: (item: FIELD_TYPE) => void;
   invalid: boolean;
   validate: () => void;
   selectedItems: FIELD_TYPE[];
};

export const useIcd9ProcedureSelector = (
   control: Control,
   defaultValues: ICD9Procedure[] | undefined,
   fieldName: FieldName<Icd9ProcedureSelectorFormBean>
): AutosuggestSelectorReturnType<ICD9Procedure> => {
   const controller = useController({
      name: fieldName,
      control: control,
      defaultValue: defaultValues ?? [],
      rules: {
         validate: requiredValidator,
      },
   });
   const {
      field: { onChange, value: selectedItems },
   } = controller;

   const addItem = useCallback(
      (item: ICD9Procedure): void => {
         if (selectedItems.filter((i: ICD9Procedure) => i.code === item.code).length > 0) {
            return;
         }
         onChange([item, ...selectedItems]);
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [selectedItems]
   );

   const removeItem = useCallback(
      (item: ICD9Procedure): void => {
         onChange(selectedItems.filter((i: ICD9Procedure) => i.code !== item.code));
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [selectedItems]
   );

   const invalid = controller.meta.invalid && !selectedItems.length;

   const validate = useCallback(async (): Promise<void> => {
      await control.trigger(fieldName);
      // Exclude control
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [fieldName]);

   return useMemo(
      () => ({
         addItem: addItem,
         removeItem: removeItem,
         invalid: invalid,
         validate: validate,
         selectedItems: selectedItems,
      }),
      [addItem, removeItem, invalid, validate, selectedItems]
   );
};
